/**
 * MockPayPage — Phase 3.
 *
 * Shown when PAYMENT_GATEWAY='mock' on the backend and a user clicks
 * "Subscribe". Acts as a stand-in for Stripe Checkout: lets the user
 * simulate either a successful or failed payment.
 *
 * URL: /subscribe/mock-pay?subscription_id=X&token=...
 */

import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  HiOutlineCreditCard,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineExclamationTriangle,
} from 'react-icons/hi2'
import { apiService } from '../../services/api'
import { usePricingMode } from '../../contexts/PricingModeContext'

export default function MockPayPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const subscriptionId = params.get('subscription_id')
  const token = params.get('token') || ''
  const { config } = usePricingMode()

  const [processing, setProcessing] = useState(null) // 'success' | 'fail' | null
  const [error, setError] = useState('')

  const annualPrice = config?.annual_price_usd || '60'

  const process = async (action) => {
    if (processing) return
    setProcessing(action)
    setError('')
    try {
      const res = await apiService.post('/mock-payments/process/', {
        subscription_id: subscriptionId,
        token,
        action,
      })
      if (res?.success) {
        const payload = res.data?.data ?? res.data
        const redirect = payload?.redirect_url || '/profile-settings?tab=subscription'
        // For success, swing through the same /subscribe/success page Stripe would land on
        if (action === 'success') {
          navigate(`/subscribe/success?session_id=mock-${subscriptionId}`)
        } else {
          navigate(redirect)
        }
      } else {
        setError(res?.data?.message || res?.error?.message || 'Mock payment failed.')
        setProcessing(null)
      }
    } catch (e) {
      setError(e?.message || 'Network error')
      setProcessing(null)
    }
  }

  if (!subscriptionId || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <HiOutlineXCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid mock-checkout link</h1>
          <p className="text-sm text-gray-600 mb-4">Subscription ID or token missing.</p>
          <Link to="/profile-settings?tab=subscription" className="text-cyan-600 hover:text-cyan-800">
            Back to subscription page
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-50 px-4 py-8 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Mock mode banner */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-3 flex items-center gap-2">
          <HiOutlineExclamationTriangle className="w-5 h-5" />
          <span className="text-sm font-bold uppercase tracking-wider">Test mode — no real payment</span>
        </div>

        <div className="p-8">
          <div className="text-center mb-6">
            <div className="inline-flex w-14 h-14 rounded-full bg-cyan-100 items-center justify-center mb-3">
              <HiOutlineCreditCard className="w-7 h-7 text-cyan-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">OVACS Annual</h1>
            <p className="text-gray-600 mt-1">
              <span className="text-3xl font-bold text-gray-900">${annualPrice}</span>
              <span className="text-sm"> / year</span>
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 mb-6 text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-gray-600">Subscription ID</span><span className="font-mono text-gray-900">#{subscriptionId}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Mock token</span><span className="font-mono text-xs text-gray-500">{token.slice(0, 16)}…</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Mode</span><span className="text-warning font-semibold">Mock gateway</span></div>
          </div>

          <p className="text-sm text-gray-600 mb-5 leading-relaxed">
            This is a <strong>test payment screen</strong> shown because the system is currently
            using the mock payment gateway. No real money will be charged. Choose an outcome
            to simulate below — both actions write a real entry to your subscription's
            event log so you can verify the full flow end-to-end.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-danger-soft border border-danger/30 rounded text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={() => process('success')}
              disabled={!!processing}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
            >
              <HiOutlineCheckCircle className="w-5 h-5" />
              {processing === 'success' ? 'Processing…' : 'Simulate successful payment'}
            </button>
            <button
              type="button"
              onClick={() => process('fail')}
              disabled={!!processing}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors"
            >
              <HiOutlineXCircle className="w-5 h-5" />
              {processing === 'fail' ? 'Processing…' : 'Simulate failed payment'}
            </button>
            <Link
              to="/profile-settings?tab=subscription"
              className="block text-center text-sm text-gray-500 hover:text-gray-700 mt-3"
            >
              Cancel and go back
            </Link>
          </div>

          <p className="text-xs text-gray-400 text-center mt-6">
            When real Stripe credentials are configured, an admin can switch the gateway in
            Subscription Settings and this page will no longer appear.
          </p>
        </div>
      </div>
    </div>
  )
}
